import React from 'react';
import MiraOrb from './MiraOrb';
import MiraOnboarding from './MiraOnboarding';
import { useVoiceComposer, VoiceBar, MicButton } from './MiraVoice';
import { useGroupSync, InviteModal, useTypingPing, TypingDots as GuestTyping, shouldReveal, SparkReveal } from './MiraGroup';
import { downscaleImage } from '../lib/downscaleImage';

/**
 * MiraWidget — floating thinking-partner.
 *
 * Props:
 *   card_code?         → passes canvas awareness to /api/mira/chat
 *   submission_id?     → tags mid-thread chats on Canvas idea pages
 *   deck?              → hard block on kids decks: {kids, long_view, little_genius}
 *   trigger?           → 'dwell' (default) — shows after ~14s | 'always' — visible on load
 *   position?          → 'br' (bottom-right, default) | 'inline'
 *   pageKey?           → localStorage scope so chats survive nav within visit
 */

const KIDS_DECKS = new Set(['kids', 'long_view', 'little_genius']);
// v2 openings — chosen based on context. Mira never repeats an intro mid-relationship.
const OPENING_CARD = "Hello, I'm Mira. Let's build this together. What was the very first thought the question gave you, even if it is incomplete?";
const OPENING_STUDIO = "Hello, I'm Mira, your ideation coach. Bring me a problem, a possibility or the beginning of an idea. What are we trying to make better?";
const OPENING_IDEA = "Tell me the idea as it stands, what you want it to become and where you feel stuck. We'll take it from there.";
// Quick starts — spanning the v2 range: generate, sharpen, challenge, choose, test.
const QUICK_ACTIONS = [
  { icon: '✨', label: 'Give me 5 sparks',   prompt: 'Give me five distinct sparks I could explore here. Make them different in principle, not just wording.' },
  { icon: '🔬', label: 'Sharpen this',       prompt: 'Help me sharpen the idea into a one-sentence concept, then the person it serves and the distinctive move.' },
  { icon: '⚡', label: 'Challenge my idea',  prompt: 'Push back hard. What assumptions am I treating as facts? What might not work? Who could dislike or be excluded by this?' },
  { icon: '🎯', label: 'Design the first test', prompt: 'Design the smallest honest version I can try next week. Who are the first five people, and what result would make us keep going, change direction or stop?' },
];
// Dining Mode quick starts — table-time energy: shorter, warmer, uses the session's powers (sketch).
const QUICK_ACTIONS_DINING = [
  { icon: '✨', label: 'Give me 5 sparks',    prompt: 'Give me five distinct sparks I could explore tonight. Make them different in principle, not just wording.' },
  { icon: '🍜', label: 'An idea from tonight', prompt: 'We\u2019re at the table right now. Help me turn something from tonight — the food, the company, the conversation — into an idea worth keeping.' },
  { icon: '✏️', label: 'Sketch my idea',      prompt: 'I\u2019ll describe my idea — then draw me a simple sketch of how it could look.' },
  { icon: '🎯', label: 'Make it real by Sunday', prompt: 'Design the smallest honest version of my idea I could try this week. Who are the first five people, and what result tells us to keep going?' },
];
const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';
const IDENTITY_KEY = 'mira:identity';  // stored globally across all conversations for this browser
// iOS Safari auto-zooms the page when a focused input's font-size is < 16px — keep entry fields at 16 on touch screens.
const MOBILE_FS = typeof window !== 'undefined' && window.innerWidth <= 768;
export const inputFs = (desktop) => (MOBILE_FS ? 16 : desktop);

function greetingFor(now = new Date()) {
  const h = now.getHours();
  if (h < 5) return 'Hello';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Hello';
}

function buildOpening({ card_code, submission_id }) {
  const g = greetingFor();
  const opener = submission_id ? OPENING_IDEA : (card_code ? OPENING_CARD : OPENING_STUDIO);
  // "Good morning, I'm Mira…" — never "Good morning. Hello, I'm Mira…"
  if (opener.startsWith('Hello, ')) return `${g}, ${opener.slice(7)}`;
  if (g === 'Hello' && opener.startsWith('Hello')) return opener;
  return `${g}. ${opener}`;
}

export default function MiraWidget({ card_code, submission_id, deck, trigger = 'dwell', position = 'br', pageKey }) {
  const blocked = deck && KIDS_DECKS.has(deck);

  const storageKey = React.useMemo(() => {
    if (pageKey) return `mira:${pageKey}`;
    if (submission_id) return `mira:idea:${submission_id}`;
    if (card_code) return `mira:card:${card_code}`;
    return 'mira:generic';
  }, [pageKey, submission_id, card_code]);

  // Lazy initial state — hydrate BEFORE any effect can persist over it (fixes reload race).
  const [visible, setVisible] = React.useState(trigger === 'always');
  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState(() => {
    try {
      if (typeof window === 'undefined') return [];
      const raw = window.localStorage.getItem(storageKey);
      const saved = raw ? JSON.parse(raw) : null;
      return (saved && Array.isArray(saved.messages)) ? saved.messages : [];
    } catch { return []; }
  });
  const [input, setInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [orbState, setOrbState] = React.useState('idle');   // idle | thinking | speaking
  const [conversationId, setConversationId] = React.useState(() => {
    try {
      if (typeof window === 'undefined') return null;
      const raw = window.localStorage.getItem(storageKey);
      const saved = raw ? JSON.parse(raw) : null;
      return (saved && saved.conversationId) || null;
    } catch { return null; }
  });
  const scrollRef = React.useRef(null);
  const abortRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Identity — name + optional email, stored globally so returning guests are known.
  const [identity, setIdentity] = React.useState(() => {
    try {
      if (typeof window === 'undefined') return { name: '', email: '' };
      const raw = window.localStorage.getItem(IDENTITY_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      return { name: (saved && saved.name) || '', email: (saved && saved.email) || '' };
    } catch { return { name: '', email: '' }; }
  });
  const [showIdentityForm, setShowIdentityForm] = React.useState(false);

  // Restore the last conversation from the server when local storage is empty
  // (browser closed / cache cleared) — the server remembers, the screen should too.
  React.useEffect(() => {
    if (messages.length > 0) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/mira/conversations`, { credentials: 'include' });
        const j = await r.json();
        const latest = (j.items || [])[0];
        if (!latest || !alive) return;
        const h = await fetch(`${BASE}/mira/history/${latest.id}`, { credentials: 'include' });
        const hj = await h.json();
        const turns = (hj.turns || []).map((t) => ({
          role: t.role === 'guest' ? 'guest' : 'mira', text: t.text || '', speaker: t.speaker }));
        if (hj.group) setGroup({ host: hj.host_name, names: [hj.host_name, ...(hj.participants || [])].filter(Boolean) });
        if (turns.length && alive) {
          setMessages((m) => (m.length ? m : turns));
          setConversationId((c) => c || latest.id);
        }
      } catch { /* offline */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Registration gate — name + mobile before any Mira chat ─
  const [reg, setReg] = React.useState(null);           // null=loading
  const [gatePulse, setGatePulse] = React.useState(0);
  React.useEffect(() => {
    fetch(`${BASE}/mira/me`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => setReg(j))
      .catch(() => setReg({ registered: false }));
  }, []);

  // ── Dining Mode ────────────────────────────────────────
  const [dining, setDining] = React.useState(null);        // session state object | null
  const [showUnlock, setShowUnlock] = React.useState(false);
  const [unlockCode, setUnlockCode] = React.useState('');
  const [unlockErr, setUnlockErr] = React.useState('');
  const [pendingPhoto, setPendingPhoto] = React.useState(null);   // dataURL
  const [sparkOpen, setSparkOpen] = React.useState(false);
  const [showOnboard, setShowOnboard] = React.useState(false);
  const [group, setGroup] = React.useState(null);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [typingName, setTypingName] = React.useState('');
  const [joinCode, setJoinCode] = React.useState('');
  const [joinMsg, setJoinMsg] = React.useState('');
  const joinByCode = async (code) => {
    try {
      const r = await fetch(`${BASE}/mira/join`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
      });
      const j = await r.json();
      if (!r.ok) {
        const d = j.detail || {};
        setJoinMsg(r.status === 403
          ? 'Sign in first (name + mobile above) — then pop the code in.'
          : (d.message || j.message || 'That code didn\u2019t work — check it with your host?'));
        return;
      }
      setConversationId(j.conversation_id);
      const h = await fetch(`${BASE}/mira/history/${j.conversation_id}`, { credentials: 'include' });
      const hj = await h.json();
      if (h.ok) {
        setMessages((hj.turns || []).map((t) => ({
          role: t.role === 'guest' ? 'guest' : 'mira', text: t.text || '', speaker: t.speaker })));
        if (hj.group) setGroup({ host: hj.host_name, names: [hj.host_name, ...(hj.participants || [])].filter(Boolean) });
      }
      setJoinMsg('');
      setJoinCode('');
    } catch {
      setJoinMsg('That code didn\u2019t work — try again?');
    }
  };
  const [reveal, setReveal] = React.useState(null);
  const myFirstRef = React.useRef('');
  myFirstRef.current = (((reg && reg.name) || identity.name || '')).split(' ')[0];
  const voice = useVoiceComposer({
    onTranscript: (t) => setInput(t),
    onAutoSend: (t) => sendText(t),
  });
  const onGroupSync = React.useCallback((j) => {
    if (j.group) setGroup({ host: j.host_name, names: [j.host_name, ...(j.participants || [])].filter(Boolean) });
    setTypingName(j.typing_name || '');
    if (j.spark_reveal && shouldReveal(j.spark_reveal, myFirstRef.current)) setReveal(j.spark_reveal);
    const turns = (j.turns || []).map((t) => ({
      role: t.role === 'guest' ? 'guest' : 'mira', text: t.text || '', speaker: t.speaker }));
    setMessages((m) => (turns.length > m.length ? turns : m));
  }, []);
  useGroupSync({ conversationId, active: !!group && open, busy, onSync: onGroupSync });
  const pingTyping = useTypingPing(conversationId, !!group);
  const photoInputRef = React.useRef(null);

  const tryUnlock = React.useCallback(async ({ token, code }) => {
    setUnlockErr('');
    try {
      const r = await fetch(`${BASE}/mira/dining/unlock`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(token ? { token } : { code }),
      });
      const j = await r.json();
      if (!r.ok) {
        setUnlockErr((j.detail && j.detail.message) || 'That code didn\u2019t work.');
        return false;
      }
      setDining(j.session);
      setShowUnlock(false);
      setVisible(true);
      return true;
    } catch { setUnlockErr('Connection hiccup — try again.'); return false; }
  }, []);

  // On mount: restore an active dining session; else auto-unlock from a table QR token.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/mira/dining/state`, { credentials: 'include' });
        const j = await r.json();
        if (cancelled) return;
        if (j.active) { setDining(j.session); setVisible(true); return; }
        const raw = window.localStorage.getItem('mira:dining_token');
        if (raw) {
          const t = JSON.parse(raw);
          if (t.token && Date.now() - (t.ts || 0) < 4 * 3600 * 1000) {
            const ok = await tryUnlock({ token: t.token });
            if (ok) window.localStorage.removeItem('mira:dining_token');
          } else {
            window.localStorage.removeItem('mira:dining_token');
          }
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [tryUnlock]);

  // Keep the dining timer honest while the widget sits open.
  React.useEffect(() => {
    if (!dining) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${BASE}/mira/dining/state`, { credentials: 'include' });
        const j = await r.json();
        setDining(j.active ? j.session : null);
      } catch { /* silent */ }
    }, 60000);
    return () => clearInterval(id);
  }, [dining ? dining.session_id : null]);

  const [rendering, setRendering] = React.useState(false);
  const [pendingDoc, setPendingDoc] = React.useState(null);
  const docInputRef = React.useRef(null);
  const pickDoc = async (file) => {
    if (!file) return;
    setPendingDoc({ name: file.name, uploading: true });
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${BASE}/mira/upload-doc`, { method: 'POST', credentials: 'include', body: fd });
      const j = await r.json();
      if (r.ok) setPendingDoc(j);
      else {
        setPendingDoc(null);
        setMessages((m) => [...m, { role: 'mira', text: (j.detail && j.detail.message) || 'That file wouldn\u2019t open — try again?' }]);
      }
    } catch {
      setPendingDoc(null);
      setMessages((m) => [...m, { role: 'mira', text: 'The upload slipped — check your connection and try again?' }]);
    }
  };
  const requestRender = async () => {
    if (rendering || !dining || dining.renders_left <= 0) return;
    setRendering(true);
    setMessages((m) => [...m, { role: 'mira', text: 'Painting your concept visual… give me a moment 🎨' }]);
    try {
      const r = await fetch(`${BASE}/mira/dining/render`, { method: 'POST', credentials: 'include' });
      const j = await r.json();
      if (r.ok && j.url) {
        setMessages((m) => [...m.slice(0, -1), { role: 'mira', text: 'Here it is — your idea, painted ✦', image: j.url }]);
        setDining((d) => (d ? { ...d, renders_left: j.renders_left } : d));
      } else {
        setMessages((m) => [...m.slice(0, -1), { role: 'mira', text: (j.detail && j.detail.message) || 'The paint didn\u2019t dry — try once more in a moment.' }]);
      }
    } catch {
      setMessages((m) => [...m.slice(0, -1), { role: 'mira', text: 'The paint didn\u2019t dry — try once more in a moment.' }]);
    } finally { setRendering(false); }
  };

  const pickPhoto = async (file) => {
    if (!file || !dining || dining.photos_left <= 0) return;
    try {
      const small = await downscaleImage(file, { maxEdge: 1400, quality: 0.8, minBytes: 200_000 });
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = rej;
        fr.readAsDataURL(small);
      });
      setPendingPhoto(dataUrl);
    } catch { /* silent */ }
  };

  const saveIdentity = (next) => {
    setIdentity(next);
    try { window.localStorage.setItem(IDENTITY_KEY, JSON.stringify(next)); } catch { /* no-op */ }
  };

  // Client-side kids-deck check: server still enforces 403 as source of truth,
  // but hiding the orb up front avoids a bad UX moment on kids-facing cards.
  const [deckBlocked, setDeckBlocked] = React.useState(false);
  React.useEffect(() => {
    if (!card_code || deck) return;
    let cancelled = false;
    fetch(`${BASE}/cards/${encodeURIComponent(card_code)}`)
      .then((r) => r.ok ? r.json() : null)
      .then((c) => { if (!cancelled && c && KIDS_DECKS.has(c.deck)) setDeckBlocked(true); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [card_code, deck]);

  React.useEffect(() => {
    if (blocked) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({ messages, conversationId }));
    } catch { /* no-op */ }
  }, [messages, conversationId, storageKey, blocked]);

  // Dwell trigger — show orb after 14s (shortened from 20 for better first-visit conversion).
  React.useEffect(() => {
    if (blocked || trigger !== 'dwell') return;
    const t = setTimeout(() => setVisible(true), 14000);
    return () => clearTimeout(t);
  }, [trigger, blocked]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const openPanel = () => {
    setOpen(true);
    try { if (!window.localStorage.getItem('mira:onboarded_v1')) setShowOnboard(true); } catch { /* no-op */ }
    if (messages.length === 0) {
      setMessages([{ role: 'mira', text: buildOpening({ card_code, submission_id }) }]);
    }
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 120);
  };
  const closePanel = () => {
    setOpen(false);
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
  };

  const startNewChat = () => {
    if (busy) return;
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    setConversationId(null);
    setMessages([{ role: 'mira', text: buildOpening({ card_code, submission_id }) }]);
    try { window.localStorage.removeItem(storageKey); } catch { /* no-op */ }
    setShowIdentityForm(false);
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 60);
  };

  const endChat = async () => {
    if (conversationId) {
      try {
        await fetch(`${BASE}/mira/end-chat`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversation_id: conversationId }),
        });
      } catch { /* silent — the record still exists */ }
    }
    // Clear local state so a fresh visit starts clean.
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    setConversationId(null);
    setMessages([]);
    try { window.localStorage.removeItem(storageKey); } catch { /* no-op */ }
    setOpen(false);
  };

  const sendText = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || busy) return;
    if (pendingDoc && pendingDoc.uploading) return;
    if (reg && !reg.registered) { setGatePulse((p) => p + 1); return; }
    setInput('');
    setBusy(true);
    setOrbState('thinking');
    const doc = pendingDoc && !pendingDoc.uploading ? pendingDoc : null;
    const myFirst = (((reg && reg.name) || identity.name || '')).split(' ')[0];
    setMessages((m) => [...m, { role: 'guest', text: trimmed, doc: doc ? doc.name : undefined, speaker: myFirst }, { role: 'mira', text: '' }]);

    const controller = new AbortController();
    abortRef.current = controller;

    const body = { message: trimmed };
    if (conversationId) body.conversation_id = conversationId;
    if (card_code) body.card_code = card_code;
    if (submission_id) body.submission_id = submission_id;
    if (identity.name) body.name = identity.name;
    if (identity.email) body.email = identity.email;
    if (pendingPhoto) { body.images = [pendingPhoto]; setPendingPhoto(null); }
    if (doc) { body.doc_id = doc.doc_id; setPendingDoc(null); }

    try {
      const resp = await fetch(`${BASE}/mira/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!resp.ok) {
        let srvMsg = '';
        try { const j = await resp.json(); srvMsg = (j.detail && j.detail.message) || ''; } catch { /* keep default */ }
        if (srvMsg) {
          setMessages((m) => {
            const next = m.slice();
            const last = next[next.length - 1];
            if (last && last.role === 'mira' && !last.text) next[next.length - 1] = { role: 'mira', text: srvMsg };
            return next;
          });
          return;
        }
        throw new Error(`HTTP ${resp.status}`);
      }
      if (!resp.body) throw new Error('no body');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buf = '';
      let first = true;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const lines = raw.split('\n');
          let ev = 'message';
          let data = '';
          for (const ln of lines) {
            if (ln.startsWith('event: ')) ev = ln.slice(7).trim();
            else if (ln.startsWith('data: ')) data += ln.slice(6);
          }
          if (!data) continue;
          let payload;
          try { payload = JSON.parse(data); } catch { continue; }
          if (ev === 'meta' && payload.conversation_id) {
            setConversationId(payload.conversation_id);
          } else if (ev === 'session') {
            setDining(payload);
          } else if (ev === 'delta' && payload.text) {
            if (first) { setOrbState('speaking'); first = false; }
            setMessages((m) => {
              const next = m.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'mira') next[next.length - 1] = { ...last, text: last.text + payload.text };
              return next;
            });
          } else if (ev === 'error') {
            setMessages((m) => {
              const next = m.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'mira' && !last.text) {
                next[next.length - 1] = { role: 'mira', text: payload.message || 'Give me a moment — my thinking got tangled. Say that again?' };
              }
              return next;
            });
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setMessages((m) => {
          const next = m.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'mira' && !last.text) {
            next[next.length - 1] = { role: 'mira', text: 'Give me a moment — my thinking got tangled. Say that again?' };
          }
          return next;
        });
      }
    } finally {
      setBusy(false);
      setOrbState('idle');
      abortRef.current = null;
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(input); } };
  const quickSend = (prompt) => sendText(prompt);

  if (blocked || deckBlocked || !visible) return null;

  const wrapStyle = position === 'inline'
    ? { position: 'relative', display: 'inline-block' }
    : (open && MOBILE_FS
        ? { position: 'fixed', inset: 0, zIndex: 1000 }
        : { position: 'fixed', right: 22, bottom: 22, zIndex: 1000 });

  const showQuickActions = messages.length <= 1; // only before the guest's first turn

  return (
    <div style={wrapStyle} data-testid="mira-widget">
      {showOnboard && <MiraOnboarding onDone={() => setShowOnboard(false)} />}
      {reveal && <SparkReveal reveal={reveal} onClose={() => setReveal(null)} />}
      {inviteOpen && conversationId && (
        <InviteModal conversationId={conversationId}
          onClose={() => setInviteOpen(false)}
          onCreated={() => {
            const me = (((reg && reg.name) || identity.name || '')).split(' ')[0];
            setGroup((g) => g || { host: me, names: [me].filter(Boolean) });
          }} />
      )}
      {!open && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          {/* Floating trigger chip */}
          <button
            onClick={openPanel}
            data-testid="mira-chip"
            aria-label="Open Mira — Feed Your Genius"
            style={{
              background: 'linear-gradient(135deg,#3A1140 0%,#5A1F5E 60%,#7A2F7C 100%)',
              color: '#F5EFD8',
              border: '1px solid rgba(232,184,75,0.4)',
              borderRadius: 999,
              padding: '9px 16px 9px 14px',
              fontSize: 13, fontWeight: 800, letterSpacing: 0.2,
              cursor: 'pointer',
              boxShadow: '0 10px 24px rgba(58,17,64,0.35), 0 0 0 1px rgba(255,255,255,0.06) inset',
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              transition: 'transform 200ms ease, box-shadow 200ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <span style={{ color: '#F5C96A', fontSize: 14 }}>✦</span>
            <span>Think with Mira</span>
          </button>

          {/* Orb with "Try Mira" caption */}
          <button
            onClick={openPanel}
            aria-label="Chat with Mira"
            data-testid="mira-open-btn"
            style={{
              border: 'none', background: 'transparent', padding: 0, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              filter: 'drop-shadow(0 10px 22px rgba(58,17,64,0.4))',
              transition: 'transform 200ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <MiraOrb state="idle" size={70} />
            <span style={{
              fontSize: 10, fontWeight: 900, letterSpacing: 1.4, textTransform: 'uppercase',
              color: '#3A1140', background: 'rgba(255,255,255,0.85)',
              padding: '2px 8px', borderRadius: 999,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              boxShadow: '0 2px 6px rgba(58,17,64,0.15)',
            }}>Try Mira</span>
          </button>
        </div>
      )}

      {open && (
        <div
          data-testid="mira-panel"
          style={{
            width: MOBILE_FS && position !== 'inline' ? '100%' : 380,
            maxWidth: MOBILE_FS && position !== 'inline' ? '100%' : 'calc(100vw - 32px)',
            height: MOBILE_FS && position !== 'inline' ? '100%' : 560,
            maxHeight: MOBILE_FS && position !== 'inline' ? '100%' : 'calc(100vh - 60px)',
            background: '#FBF7F2',
            border: '1px solid rgba(232,184,75,0.25)',
            borderRadius: MOBILE_FS && position !== 'inline' ? 0 : 22,
            boxShadow: '0 24px 60px rgba(58,17,64,0.45)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {/* Header — aubergine gradient with the orb + new chat + close */}
          <header style={{
            padding: '14px 16px',
            background: 'linear-gradient(135deg,#3A1140 0%,#531B55 55%,#7A2F7C 100%)',
            color: '#F5F1EA',
            display: 'flex', alignItems: 'center', gap: 12,
            position: 'relative',
          }}>
            <MiraOrb state={orbState} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: 0.2 }}>Mira</div>
              <div style={{ fontSize: 11, color: '#E8B84B', letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700 }}>
                {dining ? 'On the house with your meal' : 'Your ideation coach'}
              </div>
            </div>
            {dining && <ProgressRing used={dining.turns_used} max={dining.turns_max} />}
            <a href="/mira-guide.html" target="_blank" rel="noreferrer" data-testid="mira-guide-btn"
              title="How Mira works — the diner's guide"
              style={{ color: '#E8B84B', textDecoration: 'none', fontSize: 13, fontWeight: 900,
                       border: '1.5px solid rgba(232,184,75,0.5)', borderRadius: '50%',
                       width: 20, height: 20, display: 'flex', alignItems: 'center',
                       justifyContent: 'center', lineHeight: 1 }}>?</a>
            <a href="/mira" data-testid="mira-expand-btn" title="Open the full Mira studio"
              style={{ color: '#E8B84B', textDecoration: 'none', fontSize: 15, fontWeight: 900,
                       padding: '2px 6px', lineHeight: 1 }}>⤢</a>
            {conversationId && reg && reg.registered && (
              <button
                onClick={() => setInviteOpen(true)}
                aria-label="Invite friends to think together"
                data-testid="mira-invite-btn"
                title="Invite friends — think together"
                style={{
                  background: 'rgba(255,255,255,0.14)', color: '#F5C96A',
                  border: 'none', borderRadius: 999,
                  width: 32, height: 32, cursor: 'pointer', fontSize: 13, fontWeight: 900,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >+✦</button>
            )}
            <button
              onClick={startNewChat}
              disabled={busy}
              aria-label="Start a new chat"
              data-testid="mira-newchat-btn"
              title="Start a new chat"
              style={{
                background: 'rgba(255,255,255,0.14)', color: '#F5F1EA',
                border: 'none', borderRadius: 999,
                width: 32, height: 32, cursor: busy ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: busy ? 0.5 : 1,
              }}
            >
              <NewChatIcon />
            </button>
            <button
              onClick={closePanel}
              aria-label="Minimize Mira"
              data-testid="mira-close-btn"
              title="Minimize (chat continues)"
              style={{
                background: 'rgba(255,255,255,0.14)', color: '#F5F1EA',
                border: 'none', borderRadius: 999,
                width: 32, height: 32, cursor: 'pointer', fontSize: 18, lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >×</button>
          </header>

          {/* Dining strip — badge when active, unlock entry when not */}
          <div style={{
            padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8,
            background: dining ? 'linear-gradient(90deg,#2E0D33,#3A1140)' : '#F7F1E6',
            borderBottom: '1px solid ' + (dining ? 'rgba(232,184,75,0.35)' : '#EFE4C7'),
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }} data-testid="mira-dining-strip">
            {dining ? (
              <>
                <span style={{ color: '#E8B84B', fontSize: 13 }}>✦</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#F5EFD8', flex: 1 }}>
                  Think with Mira · on the house with your meal
                  {dining.table ? <span style={{ opacity: 0.6 }}> · {dining.table}</span> : null}
                </span>
                <span data-testid="mira-dining-timer" style={{
                  fontSize: 10, fontWeight: 800, color: '#E8B84B', letterSpacing: 0.5,
                  background: 'rgba(232,184,75,0.12)', border: '1px solid rgba(232,184,75,0.3)',
                  borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap',
                }}>
                  ⏱ {dining.minutes_used != null ? dining.minutes_used : Math.max(0, (dining.minutes_max || 90) - (dining.minutes_left || 0))}m
                  <span style={{ opacity: 0.6 }}> / {dining.minutes_max || 90}</span>
                </span>
                {dining.landing_window_open && (
                  <button onClick={() => setSparkOpen(true)} data-testid="mira-spark-btn"
                    style={{ background: '#E8B84B', color: '#3A1140', border: 'none', borderRadius: 999,
                             padding: '4px 10px', fontSize: 10, fontWeight: 900, cursor: 'pointer',
                             letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    Land it · Spark Card
                  </button>
                )}
              </>
            ) : !showUnlock ? (
              <button onClick={() => setShowUnlock(true)} data-testid="mira-unlock-toggle"
                style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
                         fontFamily: 'inherit', textAlign: 'left', padding: 0,
                         display: 'flex', alignItems: 'center', gap: 8 }}>
                <span data-testid="mira-tier-chip" style={{
                  fontSize: 9, fontWeight: 900, letterSpacing: 1, color: '#7A6A80',
                  background: '#EFE8F1', border: '1px solid #E0D3E4', borderRadius: 999,
                  padding: '3px 8px', whiteSpace: 'nowrap' }}>FREE STUDIO</span>
                <span style={{ flex: 1, fontSize: 11, fontWeight: 800, color: '#8A6112', lineHeight: 1.35 }}>
                  Dining at Xin? Unlock the deeper session — on the house.
                </span>
                <span style={{ fontSize: 10, fontWeight: 900, color: '#3A1140', background: '#E8B84B',
                               borderRadius: 999, padding: '4px 10px', whiteSpace: 'nowrap' }}>Unlock →</span>
              </button>
            ) : (
              <div style={{ width: '100%' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#8A6112', marginBottom: 5 }}>
                  The dining session unlocks Mira at full depth — she reads your napkin photos, sketches back, and lands your idea as a Spark Card.
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={unlockCode} onChange={(e) => setUnlockCode(e.target.value)}
                    placeholder="Today's code — e.g. MOMO-42 (it's on the board)" maxLength={40}
                    data-testid="mira-unlock-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') tryUnlock({ code: unlockCode }); }}
                    style={{ flex: 1, border: '1px solid #E0CFA0', borderRadius: 8, padding: '6px 10px',
                             fontSize: inputFs(12), outline: 'none', background: '#FFF' }} />
                  <button onClick={() => tryUnlock({ code: unlockCode })} data-testid="mira-unlock-submit"
                    style={{ background: '#3A1140', color: '#F5C96A', border: 'none', borderRadius: 8,
                             padding: '6px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                    Unlock
                  </button>
                  <button onClick={() => { setShowUnlock(false); setUnlockErr(''); }}
                    style={{ background: 'transparent', border: 'none', color: '#8A6112', cursor: 'pointer', fontSize: 14 }}>×</button>
                </div>
                {unlockErr && <div data-testid="mira-unlock-error" style={{ fontSize: 10, color: '#B33', marginTop: 4 }}>{unlockErr}</div>}
              </div>
            )}
          </div>

          {/* Identity strip retired — registration gate now captures name/mobile/email */}

          {/* Thread */}
          <div
            ref={scrollRef}
            style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', background: '#FBF7F2' }}
            data-testid="mira-thread"
          >
            {messages.map((m, i) => (
              <MiraBubble key={i} role={m.role} text={m.text} image={m.image} doc={m.doc}
                speaker={group && m.role === 'guest' ? m.speaker : undefined}
                onChoice={m.role !== 'guest' ? sendText : undefined}
                loading={busy && i === messages.length - 1 && m.role === 'mira' && !m.text && !m.image} />
            ))}
            {showQuickActions && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase',
                              color: '#8A6B92', marginBottom: 8, paddingLeft: 4 }}>
                  Quick starts
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(dining ? QUICK_ACTIONS_DINING : QUICK_ACTIONS).map((qa, i) => (
                    <button
                      key={i}
                      onClick={() => quickSend(qa.prompt)}
                      disabled={busy}
                      data-testid={`mira-quick-${i}`}
                      style={{
                        background: '#FFFFFF',
                        border: '1px solid #E4D9E7',
                        borderRadius: 999,
                        padding: '7px 12px',
                        fontSize: 12, fontWeight: 700,
                        color: '#3A1140', cursor: busy ? 'wait' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        transition: 'all 150ms ease',
                      }}
                      onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.background = '#3A1140'; e.currentTarget.style.color = '#F5EFD8'; } }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.color = '#3A1140'; }}
                    >
                      <span>{qa.icon}</span>
                      <span>{qa.label}</span>
                    </button>
                  ))}
                </div>
                <div data-testid="mira-cocreate-hint" style={{
                  marginTop: 10, paddingLeft: 4, fontSize: 10.5, fontWeight: 700,
                  color: '#8A6112', lineHeight: 1.5 }}>
                  👥 Thinking with friends? Start the chat, then tap <b>+✦</b> up top — up to
                  three can join from their own phones.
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <input
                    value={joinCode}
                    onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setJoinMsg(''); }}
                    placeholder="TABLE CODE"
                    maxLength={8}
                    data-testid="mira-widget-join-input"
                    style={{
                      flex: 1, minWidth: 0, textAlign: 'center', letterSpacing: 3, fontWeight: 900,
                      fontSize: inputFs(12), background: '#FFF', border: '1px solid #E4D9E7',
                      borderRadius: 999, color: '#3A1140', padding: '8px 12px', outline: 'none',
                    }} />
                  <button
                    onClick={() => joinCode.trim() && joinByCode(joinCode.trim())}
                    disabled={!joinCode.trim()}
                    data-testid="mira-widget-join-btn"
                    style={{
                      background: joinCode.trim() ? '#3A1140' : '#F5EFF7',
                      color: joinCode.trim() ? '#F5C96A' : '#B0A3B6',
                      border: 'none', borderRadius: 999, padding: '8px 18px',
                      fontWeight: 900, fontSize: 11, cursor: joinCode.trim() ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                    }}>Join ✦</button>
                </div>
                {joinMsg && (
                  <div data-testid="mira-widget-join-msg" style={{ marginTop: 6, fontSize: 10.5, fontWeight: 700, color: '#B33' }}>
                    {joinMsg}
                  </div>
                )}
              </div>
            )}
          </div>

          {reg && !reg.registered && (
            <RegisterGate pulse={gatePulse} onDone={(j) => setReg(j)} />
          )}
          {reg && reg.registered && !reg.email && (
            <EmailBackfillBar onDone={(em) => setReg((r) => ({ ...r, email: em }))} />
          )}

          {/* Input row — photo (dining) + mic (visual) + textarea + send arrow */}
          <div style={{ padding: 10, borderTop: '1px solid #EFE4F0', background: '#FFFFFF',
                        display: reg && (!reg.registered || !reg.email) ? 'none' : 'block' }}>
            {pendingPhoto && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }} data-testid="mira-photo-preview">
                <img src={pendingPhoto} alt="attached sketch" style={{ height: 42, borderRadius: 8, border: '1px solid #E4D9E7' }} />
                <span style={{ fontSize: 11, color: '#7A5A83', flex: 1 }}>Photo attached — it goes with your next message.</span>
                <button onClick={() => setPendingPhoto(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8A6B92' }}>×</button>
              </div>
            )}
            {pendingDoc && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }} data-testid="mira-doc-chip">
                <span style={{ fontSize: 12, background: '#F5EFF7', border: '1px solid #E4D9E7', borderRadius: 999, padding: '3px 10px', color: '#531B55', fontWeight: 700 }}>
                  📄 {pendingDoc.name}{pendingDoc.uploading ? ' — uploading…' : ' ✓ attached'}
                </span>
                <span style={{ fontSize: 10.5, color: '#8A6B92', flex: 1 }}>{pendingDoc.uploading ? 'one moment' : 'goes with your next message'}</span>
                <button onClick={() => setPendingDoc(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8A6B92' }}>×</button>
              </div>
            )}
            {!dining && messages.length >= 8 && (
              <button onClick={() => setSparkOpen(true)} data-testid="mira-spark-pill" style={{
                display: 'block', margin: '0 auto 8px', background: '#3A1140', color: '#F5C96A',
                border: '1px solid #E8B84B', borderRadius: 999, padding: '5px 14px',
                fontSize: 11, fontWeight: 900, cursor: 'pointer', letterSpacing: 0.5,
              }}>✦ Land this idea · Spark Card</button>
            )}
            <VoiceBar voice={voice}
              onEdit={() => { voice.cancelAutoSend(); if (inputRef.current) inputRef.current.focus(); }} />
            {typingName && <GuestTyping name={typingName} />}
            <input ref={docInputRef} type="file" accept=".pdf,.xlsx,.xls,.csv,.txt,.md" style={{ display: 'none' }}
              data-testid="mira-doc-input"
              onChange={(e) => { pickDoc(e.target.files && e.target.files[0]); e.target.value = ''; }} />
            {dining && (
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                data-testid="mira-photo-input"
                onChange={(e) => { pickPhoto(e.target.files && e.target.files[0]); e.target.value = ''; }} />
            )}
            {(() => {
              // On phones in Dining Mode there are too many tools for one row —
              // split them out so the text box keeps its breathing room.
              const splitTools = MOBILE_FS && !!dining;
              const toolBtns = (
                <>
                  <button type="button" data-testid="mira-doc-btn"
                    onClick={() => docInputRef.current && docInputRef.current.click()}
                    title="Hand Mira a PDF, Excel or CSV to think through"
                    style={{
                      background: '#F5EFF7', color: '#531B55', border: '1px solid #EFE4F0',
                      borderRadius: 999, width: MOBILE_FS ? 34 : 38, height: MOBILE_FS ? 34 : 38, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 15,
                    }}>📎</button>
                  {dining && (
                    <>
                      <button type="button" data-testid="mira-photo-btn"
                        onClick={() => dining.photos_left > 0 && photoInputRef.current && photoInputRef.current.click()}
                        title={dining.photos_left > 0 ? `Show Mira a sketch (${dining.photos_left} left)` : 'Photo limit reached for this session'}
                        style={{
                          background: dining.photos_left > 0 ? '#3A1140' : '#F5EFF7',
                          color: dining.photos_left > 0 ? '#F5C96A' : '#B0A3B6',
                          border: '1px solid #EFE4F0', borderRadius: 999,
                          width: MOBILE_FS ? 34 : 38, height: MOBILE_FS ? 34 : 38, cursor: dining.photos_left > 0 ? 'pointer' : 'not-allowed',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                        <CameraIcon />
                      </button>
                      {dining.renders_enabled && dining.renders_left > 0 && (
                        <button type="button" data-testid="mira-render-btn"
                          onClick={requestRender} disabled={rendering}
                          title="Turn the idea into a concept visual (1 per session)"
                          style={{
                            background: '#E8B84B', color: '#3A1140',
                            border: '1px solid #EFE4F0', borderRadius: 999,
                            width: MOBILE_FS ? 34 : 38, height: MOBILE_FS ? 34 : 38, cursor: rendering ? 'wait' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0, fontSize: 16,
                          }}>
                          ✨
                        </button>
                      )}
                    </>
                  )}
                </>
              );
              return (
                <>
                  {splitTools && (
                    <div data-testid="mira-tools-row" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      {toolBtns}
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 9.5, fontWeight: 800, color: '#B0A3B6', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                        docs · photos · render
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: MOBILE_FS ? 6 : 8, alignItems: 'center' }}>
                    {!splitTools && toolBtns}
                    <MicButton voiceState={voice.voiceState} onClick={voice.toggleRecording}
                      disabled={busy} size={MOBILE_FS ? 34 : 38} testId="mira-mic-btn" />
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => { voice.cancelAutoSend(); pingTyping(); setInput(e.target.value); }}
                      onKeyDown={onKey}
                      placeholder={dining ? 'Think out loud…' : "Say what's on your mind…"}
                      rows={1}
                      maxLength={dining ? (dining.max_input_chars || 1000) : 6000}
                      disabled={busy}
                      data-testid="mira-input"
                      style={{
                        flex: 1, minWidth: 0, resize: 'none', border: '1px solid #E4D9E7', borderRadius: 24,
                        padding: '10px 16px', fontSize: inputFs(14), fontFamily: 'inherit',
                        outline: 'none', minHeight: 42, maxHeight: 140, lineHeight: '20px',
                        background: busy ? '#F5EFF7' : '#FFFFFF',
                      }}
                    />
                    <button
                      onClick={() => sendText(input)}
                      disabled={busy || !input.trim()}
                      data-testid="mira-send-btn"
                      aria-label="Send"
                      style={{
                        background: 'linear-gradient(135deg,#3A1140,#531B55)',
                        color: '#F5C96A',
                        border: '1px solid rgba(232,184,75,0.35)',
                        borderRadius: 999, width: MOBILE_FS ? 38 : 42, height: MOBILE_FS ? 38 : 42,
                        cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
                        opacity: busy || !input.trim() ? 0.5 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: busy || !input.trim() ? 'none' : '0 4px 10px rgba(58,17,64,0.3)',
                        transition: 'transform 120ms ease',
                      }}
                      onMouseEnter={(e) => { if (!busy && input.trim()) e.currentTarget.style.transform = 'scale(1.05)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      <SendIcon />
                    </button>
                  </div>
                </>
              );
            })()}
            <div style={{ fontSize: 10, color: '#8A8093', marginTop: 6, textAlign: 'center', display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
              <span style={{ minWidth: 0 }}>Mira is an AI ideation coach. Chats are logged so we can keep improving her.</span>
              {conversationId && messages.length > 1 && (
                <button
                  onClick={endChat}
                  data-testid="mira-endchat-btn"
                  style={{
                    background: 'transparent', border: 'none', color: '#8A6B92',
                    fontSize: 10, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                    textDecoration: 'underline', padding: 0, flexShrink: 0,
                  }}
                >End & save</button>
              )}
            </div>
          </div>
        </div>
      )}
      {sparkOpen && (
        <SparkCardModal
          conversationId={conversationId}          firstName={(((reg && reg.name) || identity.name || '')).split(' ')[0]}
          outletName={dining ? (dining.outlet_name || dining.outlet) : 'Xin'}
          defaultText={lastMiraText(messages)}
          onClose={() => setSparkOpen(false)}
        />
      )}
    </div>
  );
}

export function RegisterGate({ onDone, pulse = 0, dark = false }) {
  const saved = React.useMemo(() => {
    try { return JSON.parse(window.localStorage.getItem('mira:reg') || '{}'); } catch { return {}; }
  }, []);
  const [name, setName] = React.useState(saved.name || '');
  const [mobile, setMobile] = React.useState(saved.mobile || '');
  const [email, setEmail] = React.useState(saved.email || '');
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const boxRef = React.useRef(null);

  React.useEffect(() => {
    if (pulse > 0 && boxRef.current) {
      boxRef.current.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-5px)' },
         { transform: 'translateX(5px)' }, { transform: 'translateX(0)' }],
        { duration: 240 });
    }
  }, [pulse]);

  const submit = async () => {
    setErr('');
    if (name.trim().length < 2) { setErr('Your name, so Mira knows who\u2019s thinking.'); return; }
    if (mobile.replace(/\D/g, '').length < 7) { setErr('A mobile number we can reach you on.'); return; }
    if (!email.trim().includes('@')) { setErr('Your email too — that\u2019s where your Spark Card lands.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/mira/register`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), mobile: mobile.trim(), email: email.trim() || null }),
      });
      const j = await r.json();
      if (!r.ok) { setErr((j.detail && j.detail.message) || 'That didn\u2019t go through — try again.'); return; }
      try { window.localStorage.setItem('mira:reg', JSON.stringify({ name, mobile, email })); } catch { /* */ }
      onDone({ registered: true, name: j.name, email: j.email, returning: j.returning, last_topic: j.last_topic });
    } catch { setErr('Connection hiccup — try again.'); }
    finally { setBusy(false); }
  };

  const inp = {
    width: '100%', boxSizing: 'border-box', border: '1px solid ' + (dark ? '#5A3A60' : '#E4D9E7'),
    borderRadius: 10, padding: '9px 12px', fontSize: inputFs(13), outline: 'none',
    background: dark ? '#2E0D33' : '#FFF', color: dark ? '#F5EFE8' : '#26202C',
    fontFamily: 'inherit',
  };
  return (
    <div ref={boxRef} data-testid="mira-register-gate" style={{
      padding: 14, borderTop: '1px solid ' + (dark ? '#4A2450' : '#EFE4F0'),
      background: dark ? '#3A1140' : '#FBF7F2',
      display: 'flex', flexDirection: 'column', gap: 8,
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{ fontSize: 13, fontWeight: 900, color: dark ? '#F5EFE8' : '#3A1140' }}>
        Who's thinking tonight? ✦
      </div>
      <div style={{ fontSize: 11, color: dark ? '#CBB8D0' : '#8A6B92', marginTop: -4 }}>
        Name, number &amp; email — so Mira remembers you and mails you your Spark Card.
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
        maxLength={80} data-testid="mira-reg-name" style={inp} />
      <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile number"
        maxLength={20} inputMode="tel" data-testid="mira-reg-mobile" style={inp} />
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email — for your Spark Card & votes"
        maxLength={200} inputMode="email" data-testid="mira-reg-email" style={inp} />
      {err && <div data-testid="mira-reg-error" style={{ fontSize: 11, color: '#C1394A' }}>{err}</div>}
      <button onClick={submit} disabled={busy} data-testid="mira-reg-submit" style={{
        background: '#E8B84B', color: '#3A1140', border: 'none', borderRadius: 999,
        padding: '10px 0', fontWeight: 900, fontSize: 13, cursor: busy ? 'wait' : 'pointer',
      }}>
        {busy ? 'One moment…' : 'Start thinking with Mira →'}
      </button>
      <div style={{ fontSize: 10, color: dark ? '#8A6B92' : '#A895AE', textAlign: 'center' }}>
        Free · takes 10 seconds · no password ever
      </div>
    </div>
  );
}

export function EmailBackfillBar({ dark, onDone }) {
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const save = async () => {
    if (!email.trim().includes('@')) { setErr('That email doesn\u2019t look right.'); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${BASE}/mira/attach-email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const j = await r.json();
      if (r.ok) onDone(j.email);
      else setErr((j.detail && j.detail.message) || 'Try again?');
    } catch { setErr('Connection hiccup — try again.'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="mira-email-backfill" style={{
      padding: 14, borderTop: '1px solid ' + (dark ? '#4A2450' : '#EFE4F0'),
      background: dark ? '#3A1140' : '#FBF7F2',
      fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ fontSize: 12.5, fontWeight: 900, color: dark ? '#F5EFE8' : '#3A1140' }}>
        One quick thing ✦
      </div>
      <div style={{ fontSize: 11, color: dark ? '#CBB8D0' : '#8A6B92', margin: '2px 0 8px' }}>
        Your email — so your Spark Card, its votes and Xin news reach you.
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
          inputMode="email" maxLength={200} data-testid="mira-backfill-email"
          onKeyDown={(e) => e.key === 'Enter' && save()}
          style={{ flex: 1, boxSizing: 'border-box', border: '1px solid ' + (dark ? '#5A3A60' : '#E4D9E7'),
                   borderRadius: 10, padding: '9px 12px', fontSize: inputFs(13), outline: 'none',
                   background: dark ? '#2E0D33' : '#FFF', color: dark ? '#F5EFE8' : '#26202C',
                   fontFamily: 'inherit' }} />
        <button onClick={save} disabled={busy} data-testid="mira-backfill-save"
          style={{ background: '#E8B84B', color: '#3A1140', border: 'none', borderRadius: 999,
                   padding: '9px 18px', fontWeight: 900, fontSize: 12.5, cursor: 'pointer' }}>
          {busy ? '…' : 'Save →'}
        </button>
      </div>
      {err && <div data-testid="mira-backfill-error" style={{ fontSize: 11, color: '#C1394A', marginTop: 5 }}>{err}</div>}
    </div>
  );
}

function lastMiraText(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'mira' && messages[i].text) {
      return messages[i].text.replace(/```svg[\s\S]*?```/g, '').trim().slice(0, 1800);
    }
  }
  return '';
}

function ProgressRing({ used, max }) {
  const pct = Math.min(1, (used || 0) / Math.max(1, max || 40));
  const r = 12, c = 2 * Math.PI * r;
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" data-testid="mira-progress-ring" aria-label="session progress">
      <circle cx="16" cy="16" r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="3" />
      <circle cx="16" cy="16" r={r} fill="none" stroke="#E8B84B" strokeWidth="3"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
        strokeLinecap="round" transform="rotate(-90 16 16)" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function parseSpark(raw) {
  let t = (raw || '').replace(/```[\s\S]*?```/g, '').trim();
  let title = '';
  const tm = t.match(/\*\*Title:?\*\*:?\s*([^\n*]+)/i) || t.match(/^#+\s*(?!\s*SPARK)([^\n#]+)/m);
  if (tm) title = tm[1].trim().replace(/["“”]/g, '');
  t = t.replace(/^#+[^\n]*$/gm, '')
       .replace(/\*\*Title:?\*\*:?[^\n]*/i, '')
       .replace(/\*\*([^*]+)\*\*:?/g, '$1')
       .replace(/^\s*[-*]\s+/gm, '• ')
       .replace(/\n{3,}/g, '\n\n').trim();
  return { title: title || 'My Spark', body: t };
}

const PG_W = 1080, PG_H = 1350;
const CREAM = '#FBF7F2', AUB = '#3A1140', GOLD = '#B8860B', GOLD_BRIGHT = '#E8B84B', WINE = '#7C2B29';

function frame(ctx) {
  ctx.fillStyle = CREAM; ctx.fillRect(0, 0, PG_W, PG_H);
  ctx.strokeStyle = AUB; ctx.lineWidth = 4; ctx.strokeRect(46, 46, PG_W - 92, PG_H - 92);
  ctx.strokeStyle = 'rgba(184,134,11,0.65)'; ctx.lineWidth = 2; ctx.strokeRect(62, 62, PG_W - 124, PG_H - 124);
}

function footerBand(ctx, pageNo, total) {
  ctx.fillStyle = AUB; ctx.fillRect(62, PG_H - 150, PG_W - 124, 88);
  ctx.fillStyle = GOLD_BRIGHT; ctx.font = '900 22px system-ui'; ctx.textAlign = 'center';
  ctx.fillText('XIN BY MINDESCAPES®  ·  FEED YOUR GENIUS', PG_W / 2, PG_H - 112);
  ctx.fillStyle = 'rgba(245,239,232,0.75)'; ctx.font = '600 20px system-ui';
  ctx.fillText(`xin.community/canvas${total > 1 ? `   ·   ${pageNo} / ${total}` : ''}`, PG_W / 2, PG_H - 80);
}

export function SparkCardModal({ firstName, outletName, defaultText, onClose, conversationId }) {
  const [text, setText] = React.useState((defaultText || '').slice(0, 1800));
  const canvasRefs = React.useRef([]);
  const [pageLines, setPageLines] = React.useState([]);

  React.useEffect(() => {
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.font = '600 40px Georgia, serif';
    const { body } = parseSpark(text);
    const lines = [];
    for (const para of (body || 'My spark…').split(/\n\n+/)) {
      for (const seg of para.split('\n')) {
        let line = '';
        for (const w of seg.split(/\s+/).filter(Boolean)) {
          const t2 = line ? line + ' ' + w : w;
          if (ctx.measureText(t2).width > PG_W - 260) { lines.push(line); line = w; } else line = t2;
        }
        if (line) lines.push(line);
      }
      lines.push('');
    }
    while (lines.length && lines[lines.length - 1] === '') lines.pop();
    const per = 16, pgs = [];
    for (let i = 0; i < lines.length && pgs.length < 3; i += per) pgs.push(lines.slice(i, i + per));
    setPageLines(pgs);
  }, [text]);

  const totalPages = 1 + pageLines.length;

  const draw = React.useCallback(() => {
    const { title } = parseSpark(text);
    // Cover — Xin menu theme
    const cv0 = canvasRefs.current[0];
    if (cv0) {
      const ctx = cv0.getContext('2d');
      frame(ctx);
      ctx.textAlign = 'center';
      ctx.fillStyle = GOLD; ctx.font = '900 30px system-ui';
      ctx.fillText('✦  S P A R K   C A R D  ✦', PG_W / 2, 200);
      ctx.fillStyle = WINE; ctx.font = 'italic 600 26px Georgia, serif';
      ctx.fillText(`Thought through with Mira · ${outletName}`, PG_W / 2, 252);
      ctx.fillStyle = AUB; ctx.font = '700 76px Georgia, serif';
      let line = '', ty = 560; const tl = [];
      for (const w of title.split(/\s+/)) {
        const t2 = line ? line + ' ' + w : w;
        if (ctx.measureText(t2).width > PG_W - 260) { tl.push(line); line = w; } else line = t2;
        if (tl.length > 4) break;
      }
      if (line && tl.length <= 4) tl.push(line);
      ty -= (tl.length - 1) * 46;
      tl.forEach((l) => { ctx.fillText(l, PG_W / 2, ty); ty += 92; });
      ctx.fillStyle = GOLD; ctx.font = '400 40px Georgia, serif';
      ctx.fillText('✦ ─────── ✦ ─────── ✦', PG_W / 2, ty + 40);
      if (firstName) {
        ctx.fillStyle = WINE; ctx.font = 'italic 700 44px Georgia, serif';
        ctx.fillText(`— ${firstName}`, PG_W / 2, ty + 140);
      }
      footerBand(ctx, 1, totalPages);
    }
    // Body pages
    pageLines.forEach((lines, pi) => {
      const cv = canvasRefs.current[pi + 1];
      if (!cv) return;
      const ctx = cv.getContext('2d');
      frame(ctx);
      ctx.textAlign = 'center';
      ctx.fillStyle = GOLD; ctx.font = '900 24px system-ui';
      ctx.fillText(`✦  ${title.toUpperCase().slice(0, 40)}  ✦`, PG_W / 2, 160);
      ctx.fillStyle = AUB; ctx.font = '600 40px Georgia, serif';
      let y = 280;
      lines.forEach((l) => {
        if (l) ctx.fillText(l, PG_W / 2, y);
        y += l ? 58 : 34;
      });
      footerBand(ctx, pi + 2, totalPages);
    });
  }, [text, firstName, outletName, pageLines, totalPages]);

  React.useEffect(() => { draw(); }, [draw]);

  const pageBlobs = async () => {
    const out = [];
    for (let i = 0; i < totalPages; i++) {
      const cv = canvasRefs.current[i];
      if (cv) out.push(await new Promise((res) => cv.toBlob(res, 'image/png')));
    }
    return out;
  };

  const download = async () => {
    const blobs = await pageBlobs();
    blobs.forEach((blob, i) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.download = `xin-spark-card-${i + 1}.png`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    });
  };

  const sentRef = React.useRef(false);
  const [homeStatus, setHomeStatus] = React.useState('');
  const sendCardHome = async () => {
    if (sentRef.current) return;
    sentRef.current = true;
    try {
      const blobs = await pageBlobs();
      const pages = await Promise.all(blobs.map((b) => new Promise((res) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(b);
      })));
      const r = await fetch(`${BASE}/mira/spark-card`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pages, title: parseSpark(text).title, conversation_id: conversationId }),
      });
      const j = await r.json();
      if (r.ok) setHomeStatus(j.emailed ? '✦ Also emailed to you & kept for the Xin team' : '✦ Kept for the Xin team');
      else sentRef.current = false;
    } catch { sentRef.current = false; }
  };

  const [shared, setShared] = React.useState('');
  const shareText = `"${parseSpark(text).title}" — thought through with Mira at Xin ✦ xin.community/mira`;

  const share = async () => {
    // 1. Native sheet WITH all card pages (iOS 15+, Android Chrome)
    try {
      const blobs = await pageBlobs();
      const files = blobs.map((b, i) => new File([b], `xin-spark-card-${i + 1}.png`, { type: 'image/png' }));
      if (navigator.canShare && navigator.canShare({ files })) {
        await navigator.share({ files, title: 'My Xin Spark Card', text: shareText });
        setShared('Shared ✦');
        return;
      }
    } catch (e) { if (e && e.name === 'AbortError') return; }
    // 2. Native sheet, text only (in-app browsers that allow share but not files)
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My Xin Spark Card', text: shareText, url: 'https://xin.community/mira' });
        setShared('Shared — tap "Save my Spark Card" to grab the pages too ✦');
        return;
      }
    } catch (e) { if (e && e.name === 'AbortError') return; }
    // 3. Last resort: save the pages + open WhatsApp with the text
    await download();
    setShared('Pages saved to your photos — attach them in WhatsApp or your story ✦');
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(20,5,25,0.75)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
    }} data-testid="spark-card-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#FBF7F2', borderRadius: 20, padding: 14, width: 380, maxWidth: '96vw',
        maxHeight: '88vh', overflowY: 'auto', fontFamily: 'system-ui, sans-serif',
        WebkitOverflowScrolling: 'touch',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontWeight: 900, color: '#3A1140', fontSize: 15 }}>Land your spark ✦</div>
          <button onClick={onClose} data-testid="spark-card-close" aria-label="Close"
            style={{ background: '#EFE4F0', border: 'none', borderRadius: 999, width: 28, height: 28,
                     fontSize: 14, color: '#3A1140', cursor: 'pointer', fontWeight: 800 }}>×</button>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1800}
          data-testid="spark-card-text"
          placeholder="Your idea, in your own words…"
          style={{ width: '100%', border: '1px solid #E4D9E7', borderRadius: 10, padding: 10,
                   fontSize: inputFs(13), fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
        {totalPages > 1 && (
          <div style={{ fontSize: 10.5, color: '#8A6B92', fontWeight: 700, margin: '8px 0 2px', textAlign: 'center' }}
            data-testid="spark-card-pagecount">
            Your spark became a {totalPages}-page card — swipe the pages →
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollSnapType: 'x mandatory',
                      WebkitOverflowScrolling: 'touch', margin: '10px 0' }}
          data-testid="spark-card-pages">
          {Array.from({ length: totalPages }).map((_, i) => (
            <canvas key={i} ref={(el) => { canvasRefs.current[i] = el; }} width={1080} height={1350}
              style={{ width: totalPages > 1 ? '88%' : '100%', flexShrink: 0, borderRadius: 12,
                       display: 'block', scrollSnapAlign: 'center', border: '1px solid #E4D9E7' }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { sendCardHome(); download(); }} data-testid="spark-card-download"
            style={{ flex: 1, background: '#3A1140', color: '#F5C96A', border: 'none', borderRadius: 999,
                     padding: '10px 0', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
            Save my Spark Card
          </button>
          <a href="/challenges" data-testid="spark-card-canvas-link" onClick={() => sendCardHome()}
            style={{ flex: 1, background: '#E8B84B', color: '#3A1140', borderRadius: 999,
                     padding: '10px 0', fontWeight: 800, fontSize: 13, textAlign: 'center',
                     textDecoration: 'none' }}>
            Feed the Canvas →
          </a>
        </div>
        <button onClick={() => { sendCardHome(); share(); }} data-testid="spark-card-share"
          style={{ width: '100%', marginTop: 8, background: '#25D366', color: '#0B3D22',
                   border: 'none', borderRadius: 999, padding: '10px 0', fontWeight: 800,
                   fontSize: 13, cursor: 'pointer' }}>
          Share it — WhatsApp · Instagram story ↗
        </button>
        {homeStatus && (
          <div data-testid="spark-card-home-status" style={{ fontSize: 11, color: '#3E6653', fontWeight: 700, marginTop: 6, textAlign: 'center' }}>
            {homeStatus}
          </div>
        )}
        {shared && (
          <div data-testid="spark-card-share-status" style={{ fontSize: 11, color: '#3E6653', fontWeight: 700, marginTop: 6, textAlign: 'center' }}>
            {shared}
          </div>
        )}
        <SparkEmailCapture />
        <div style={{ fontSize: 10, color: '#8A6B92', marginTop: 8, textAlign: 'center' }}>
          In your voice, always. Post it on any challenge card to enter the wall.
        </div>
      </div>
    </div>
  );
}

function SparkEmailCapture() {
  const [email, setEmail] = React.useState('');
  const [state, setState] = React.useState('idle'); // idle | busy | done | hidden
  React.useEffect(() => {
    fetch(`${BASE}/mira/me`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => { if (j.email) setState('hidden'); })
      .catch(() => {});
  }, []);
  if (state === 'hidden') return null;
  if (state === 'done') {
    return (
      <div data-testid="spark-email-done" style={{ fontSize: 11.5, color: '#3E6653', fontWeight: 700, marginTop: 8, textAlign: 'center' }}>
        ✓ Done — Mira will email your card and its overnight votes tomorrow morning.
      </div>
    );
  }
  const submit = async () => {
    const v = email.trim();
    if (!v.includes('@')) return;
    setState('busy');
    try {
      const r = await fetch(`${BASE}/mira/attach-email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: v }),
      });
      setState(r.ok ? 'done' : 'idle');
    } catch { setState('idle'); }
  };
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 8 }} data-testid="spark-email-capture">
      <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
        placeholder="you@email — get your card + overnight votes"
        data-testid="spark-email-input"
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        style={{ flex: 1, border: '1.5px solid #E4D9E7', borderRadius: 999, padding: '8px 14px',
                 fontSize: 12, fontFamily: 'inherit', outline: 'none', color: '#26202C' }} />
      <button onClick={submit} disabled={state === 'busy'} data-testid="spark-email-submit"
        style={{ background: '#3A1140', color: '#F5C96A', border: 'none', borderRadius: 999,
                 padding: '8px 16px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
        {state === 'busy' ? '…' : '✉ Email me'}
      </button>
    </div>
  );
}

function MiraBubble({ role, text, loading, image, doc, onChoice, speaker }) {
  const mine = role === 'guest';
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
      <div
        data-testid={`mira-msg-${role}`}
        style={{
          maxWidth: '90%',
          padding: '10px 14px',
          borderRadius: 16,
          fontSize: 14, lineHeight: 1.55,
          background: mine
            ? 'linear-gradient(135deg,#3A1140,#531B55)'
            : '#FFFFFF',
          color: mine ? '#F5F1EA' : '#26202C',
          border: mine ? 'none' : '1px solid #EFE4F0',
          boxShadow: mine
            ? '0 4px 10px rgba(58,17,64,0.25)'
            : '0 1px 2px rgba(58,17,64,0.06)',
          wordBreak: 'break-word',
        }}
      >
        {speaker && (
          <div data-testid="mira-msg-speaker" style={{
            fontSize: 9.5, fontWeight: 900, letterSpacing: 1.2, color: '#E8B84B',
            marginBottom: 3, textTransform: 'uppercase' }}>{speaker}</div>
        )}
        {loading ? <TypingDots /> : (
          <>
            {doc && (
              <div data-testid="mira-msg-doc" style={{
                fontSize: 11, fontWeight: 800, borderRadius: 999, padding: '3px 10px',
                display: 'inline-block', marginBottom: 6,
                color: mine ? '#E8B84B' : '#531B55',
                background: mine ? 'rgba(232,184,75,0.15)' : '#F5EFF7',
                border: mine ? '1px solid rgba(232,184,75,0.4)' : '1px solid #E4D9E7',
              }}>📄 {doc}</div>
            )}
            {image && (
              <img src={image} alt="Concept visual" data-testid="mira-render-img"
                style={{ width: '100%', borderRadius: 10, marginBottom: text ? 8 : 0, display: 'block' }} />
            )}
            <FormattedText text={text} mine={mine} onChoice={onChoice} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Minimal markdown-lite renderer for Mira's bubble.
 *   **bold** → <strong>
 *   *italic* / _italic_ → <em>
 *   Paragraphs on blank lines, single \n → <br>
 *   Lines starting with `- ` or `* ` render as bullet items.
 *   Numbered `1.` `2.` lines render as ordered items.
 * Kept intentionally tiny — Mira's responses are prose, not code.
 */
function MiraSketch({ svg }) {
  const valid = React.useMemo(() => {
    try {
      const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
      return !doc.querySelector('parsererror') && doc.documentElement.tagName === 'svg';
    } catch { return false; }
  }, [svg]);
  if (!valid) {
    return (
      <div data-testid="mira-sketch-fallback" style={{
        margin: '8px 0', padding: '10px 12px', borderRadius: 10, background: '#F7F1E6',
        border: '1px dashed #D9C48A', fontSize: 12, color: '#8A6112', fontStyle: 'italic',
      }}>
        ✏️ The sketch got smudged mid-stroke — say <b>"redraw it simpler"</b> and Mira will try again.
      </div>
    );
  }
  return (
    <img data-testid="mira-sketch"
      src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
      alt="Mira's sketch"
      style={{ width: '100%', borderRadius: 10, margin: '8px 0', background: '#FFF', border: '1px solid #EFE4F0' }} />
  );
}

export function FormattedText({ text, mine, onChoice }) {
  // If a ```svg fence opened but never closed (stream cut), hide the raw code.
  let safe = text || '';
  const open = safe.lastIndexOf('```svg');
  if (open !== -1 && safe.indexOf('```', open + 6) === -1) {
    safe = safe.slice(0, open) + '\n```svg<svg/>```';   // placeholder → fallback chip
  }
  // Hide an unfinished ```choices fence while it streams.
  const co = safe.lastIndexOf('```choices');
  if (co !== -1 && safe.indexOf('```', co + 10) === -1) {
    safe = safe.slice(0, co);
  }
  const segments = safe.split(/(```svg[\s\S]*?```|```choices[\s\S]*?```)/g);
  return (
    <>
      {segments.map((seg, si) => {
        if (seg.startsWith('```svg')) {
          const svg = seg.replace(/^```svg\s*/, '').replace(/```$/, '').trim();
          if (svg === '<svg/>') return <MiraSketch key={`svg${si}`} svg="broken" />;
          return <MiraSketch key={`svg${si}`} svg={svg} />;
        }
        if (seg.startsWith('```choices')) {
          const body = seg.replace(/^```choices\s*/, '').replace(/```$/, '').trim();
          return <ChoiceCard key={`ch${si}`} body={body} onChoice={onChoice} />;
        }
        if (!seg.trim()) return null;
        return <FormattedProse key={si} text={seg} mine={mine} />;
      })}
    </>
  );
}

function ChoiceCard({ body, onChoice }) {
  const [picked, setPicked] = React.useState('');
  const lines = (body || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const question = (lines[0] || '').replace(/^Q:\s*/i, '');
  const opts = lines.slice(1).map((l) => l.replace(/^[-*]\s*/, '')).filter(Boolean);
  if (!opts.length) return null;
  const letters = 'abcdefgh';
  const pick = (opt) => {
    if (picked || !onChoice) return;
    setPicked(opt);
    onChoice(opt);
  };
  return (
    <div data-testid="mira-choice-card" style={{
      border: '1.5px solid #E4D9E7', borderRadius: 14, padding: '10px 12px',
      margin: '8px 0 4px', background: '#FDFBF7' }}>
      {question && (
        <div style={{ fontWeight: 800, fontSize: 12.5, color: '#3A1140', marginBottom: 8 }}>{question}</div>
      )}
      {opts.map((opt, i) => {
        const isPicked = picked === opt;
        return (
          <button key={i} onClick={() => pick(opt)} data-testid={`mira-choice-${i}`}
            disabled={!!picked && !isPicked}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', textAlign: 'left',
              background: isPicked ? '#3A1140' : '#F5EFF7', color: isPicked ? '#F5C96A' : '#3A1140',
              border: 'none', borderRadius: 10, padding: '8px 10px', marginBottom: 6,
              fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
              cursor: picked ? 'default' : 'pointer', opacity: picked && !isPicked ? 0.45 : 1,
              transition: 'background-color .15s, opacity .15s',
            }}>
            <span style={{
              flexShrink: 0, width: 18, height: 18, borderRadius: '50%', fontSize: 10, fontWeight: 900,
              background: isPicked ? '#E8B84B' : '#FFF', color: '#3A1140',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid #D9CDE0', marginTop: 1 }}>
              {isPicked ? '✓' : letters[i]}
            </span>
            <span>{opt}</span>
          </button>
        );
      })}
      {!picked && (
        <div style={{ fontSize: 10.5, color: '#8A6B92', marginTop: 2 }}>
          tap one — or just type your own answer below
        </div>
      )}
    </div>
  );
}

function FormattedProse({ text, mine }) {
  const paragraphs = (text || '').split(/\n{2,}/);
  return (
    <>
      {paragraphs.map((p, pi) => {
        const lines = p.split('\n');
        const isBulleted = lines.every((l) => /^\s*[-*]\s+/.test(l)) && lines.length > 1;
        const isNumbered = lines.every((l) => /^\s*\d+\.\s+/.test(l)) && lines.length > 1;
        if (isBulleted) {
          return (
            <ul key={pi} style={{ margin: '4px 0 4px 18px', padding: 0 }}>
              {lines.map((l, i) => (
                <li key={i} style={{ margin: '2px 0' }}>{renderInline(l.replace(/^\s*[-*]\s+/, ''), mine)}</li>
              ))}
            </ul>
          );
        }
        if (isNumbered) {
          return (
            <ol key={pi} style={{ margin: '4px 0 4px 20px', padding: 0 }}>
              {lines.map((l, i) => (
                <li key={i} style={{ margin: '2px 0' }}>{renderInline(l.replace(/^\s*\d+\.\s+/, ''), mine)}</li>
              ))}
            </ol>
          );
        }
        return (
          <p key={pi} style={{ margin: pi === 0 ? '0 0 8px' : '8px 0' }}>
            {lines.map((l, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {renderInline(l, mine)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

function renderInline(str, mine) {
  // Tokenise on **bold** and *italic*/_italic_
  const parts = [];
  const re = /(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(_[^_\n]+_)/g;
  let last = 0; let m; let key = 0;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) parts.push(str.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`b${key++}`} style={{ color: mine ? '#F5C96A' : '#3A1140' }}>{tok.slice(2, -2)}</strong>);
    } else {
      parts.push(<em key={`i${key++}`}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < str.length) parts.push(str.slice(last));
  return parts;
}

function TypingDots() {
  return (
    <span aria-label="Mira is thinking" style={{ display: 'inline-flex', gap: 4, alignItems: 'center', height: 20 }}>
      <style>{`
        @keyframes mira-dot { 0%,80%,100% { transform: translateY(0); opacity: 0.35; } 40% { transform: translateY(-4px); opacity: 1; } }
      `}</style>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: '50%', background: '#8A6B92', display: 'inline-block',
          animation: `mira-dot 1.2s ease-in-out ${i * 0.15}s infinite`,
        }} />
      ))}
    </span>
  );
}

function NewChatIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h10" />
      <line x1="16" y1="4" x2="22" y2="4" />
      <line x1="19" y1="1" x2="19" y2="7" />
    </svg>
  );
}

function IdentityStrip({ identity, open, onToggle, onSave }) {
  const [n, setN] = React.useState(identity.name || '');
  const [e, setE] = React.useState(identity.email || '');
  React.useEffect(() => { setN(identity.name || ''); setE(identity.email || ''); }, [identity]);

  const has = !!(identity.name || identity.email);

  return (
    <div style={{
      padding: open ? '10px 12px' : '6px 12px',
      background: '#FBF3E4',
      borderBottom: '1px solid #EFE4C7',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {!open && (
        <button
          onClick={onToggle}
          data-testid="mira-identity-toggle"
          style={{
            width: '100%', background: 'transparent', border: 'none',
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            fontSize: 11, color: '#7A5A83', fontWeight: 700, letterSpacing: 0.4,
            padding: '2px 0',
          }}
        >
          <span style={{ fontSize: 14 }}>{has ? '✓' : '✎'}</span>
          <span style={{ flex: 1, textAlign: 'left' }}>
            {has ? (
              <span>
                <span style={{ color: '#3A1140' }}>{identity.name || 'no name'}</span>
                {identity.email ? <span style={{ color: '#8A6B92' }}> · {identity.email}</span> : null}
              </span>
            ) : (
              'Sign your work (optional) — name + email so Mira can pick up where you left off'
            )}
          </span>
          <span style={{ fontSize: 10, opacity: 0.6 }}>{has ? 'edit' : 'add'}</span>
        </button>
      )}
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.4, textTransform: 'uppercase', fontWeight: 800, color: '#7A5A83' }}>
            Sign your work (optional)
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={n}
              onChange={(ev) => setN(ev.target.value)}
              placeholder="Your name"
              maxLength={80}
              data-testid="mira-identity-name"
              style={idInputStyle}
            />
            <input
              value={e}
              onChange={(ev) => setE(ev.target.value)}
              placeholder="Email (optional)"
              maxLength={200}
              type="email"
              data-testid="mira-identity-email"
              style={idInputStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button onClick={onToggle} data-testid="mira-identity-cancel" style={idBtnGhost}>Cancel</button>
            <button
              onClick={() => onSave({ name: n.trim(), email: e.trim().toLowerCase() })}
              data-testid="mira-identity-save"
              style={idBtnPrimary}
              disabled={!n.trim() && !e.trim()}
            >Save</button>
          </div>
          <div style={{ fontSize: 10, color: '#8A6B92' }}>
            We only use this to attribute your chat to you. Never shared.
          </div>
        </div>
      )}
    </div>
  );
}

const idInputStyle = {
  flex: 1, minWidth: 0, border: '1px solid #E4D9E7', borderRadius: 8,
  padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', outline: 'none',
  background: '#FFFFFF',
};
const idBtnGhost = {
  background: 'transparent', border: '1px solid #D0BCD6', borderRadius: 8,
  padding: '6px 12px', fontSize: 11, fontWeight: 700, color: '#7A5A83', cursor: 'pointer',
};
const idBtnPrimary = {
  background: '#3A1140', color: '#F5C96A', border: 'none', borderRadius: 8,
  padding: '6px 14px', fontSize: 11, fontWeight: 800, cursor: 'pointer',
};

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22l-4-9-9-4z" />
    </svg>
  );
}
